import { useMemo } from "react";

import HeadingWithHighlight from "@/components/heading-with-highlight";
import MobileHeroIcon from "@/components/mobile-hero-icon";
import type { SearchFormProps } from "@/components/search-form";
import SearchForm from "@/components/search-form";
import useHeroImage from "@/hooks/use-hero-image";
import { cn } from "@/lib/utils";

import { HeroIcon } from "./icons";

export type SearchProps = {
  description: string;
  title: string;
} & SearchFormProps;

export default function Search({ description, title, ...props }: SearchProps) {
  const heroIndex = useHeroImage();

  const heroClassName = useMemo(() => {
    const sceneWidth = 6801 / 5;
    const classNames = [
      "left-0",
      `-left-[${sceneWidth}px]`,
      "left-1/2 -translate-x-1/2",
      `-right-[${sceneWidth}px]`,
      "right-0",
    ];

    return classNames[heroIndex];
  }, [heroIndex]);

  return (
    <section
      className={
        "relative h-203 overflow-hidden pt-30 text-center md:h-225 md:pt-[150px]"
      }
    >
      <div className="relative z-1 mx-auto max-w-[858px] px-6">
        <HeadingWithHighlight
          as="h1"
          className="px-10 text-32/[1.31em] text-shadow-0-0-10 text-shadow-dark-jungle/39 md:text-60/[1.2em]"
          title={title}
        />
        <p className="mt-5 text-13/[1.85em] text-pale-lavender text-shadow-0-0-10 text-shadow-dark-jungle/39 md:mt-[30px] md:text-20/[1.5em]">
          {description}
        </p>
        <SearchForm className="mt-[30px]" {...props} />
      </div>
      {heroIndex !== -1 && (
        <>
          <div
            className={cn(
              "absolute bottom-0 hidden h-225 w-[6801px] md:block",
              heroClassName
            )}
          >
            <HeroIcon className="size-full" />
          </div>
          <MobileHeroIcon className="md:hidden" />
        </>
      )}
    </section>
  );
}
